import { Button, Checkbox, Combobox, InputBase, Loader, Stack, useCombobox } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { Right } from 'components/Right';
import dayjs from 'dayjs';
import { tKeys, useTranslate } from 'locales/index';
import { AnimatePresence, motion } from 'motion/react';
import { useRef, useState } from 'react';
import { useCreateReason, useLoadReasons } from '~/modules/Settings/Variables/Reasons/api';

const PROMISE_OF_PAYMENT_UID = 'promise_of_payment';

export type PauseReasonFormValues = { reason_id: string; end_at?: string };

type PauseReasonFormProps = {
  onSubmit: (values: PauseReasonFormValues) => unknown;
};

export const PauseReasonForm = ({ onSubmit }: PauseReasonFormProps) => {
  const t = useTranslate();

  const { reasons } = useLoadReasons({ reasonType: 'pause' });
  const { createReason, isCreateReasonLoading } = useCreateReason();

  const isCreatingReasonRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasEndDate, setHasEndDate] = useState(false);
  const [search, setSearch] = useState('');

  const form = useForm<PauseReasonFormValues>({
    initialValues: { reason_id: '', end_at: '' },
    validate: {
      reason_id: value => (value ? null : t(tKeys.ERROR.FIELD_REQUIRED)),
    },
  });

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const reasonsList = reasons.data;

  const filteredReasons = reasonsList.filter(reason =>
    reason.name.toLowerCase().includes(search.toLowerCase().trim()),
  );

  const hasExactMatch = reasonsList.some(
    reason => reason.name.toLowerCase() === search.toLowerCase().trim(),
  );
  const canCreate = search.trim().length > 0 && !hasExactMatch;

  const handleOptionSubmit = (value: string) => {
    if (value === '$create') {
      if (isCreatingReasonRef.current) return;
      isCreatingReasonRef.current = true;
      createReason(
        { name: search.trim(), reasonType: 'pause' },
        {
          onError: () => {
            isCreatingReasonRef.current = false;
          },
          onSuccess: newReason => {
            if (!isCreatingReasonRef.current) return;
            form.setFieldValue('reason_id', String(newReason.id));
            setHasEndDate(newReason.uid === PROMISE_OF_PAYMENT_UID);
            combobox.closeDropdown();
          },
        },
      );
      return;
    }

    isCreatingReasonRef.current = false;

    form.setFieldValue('reason_id', value);
    const selected = reasonsList.find(reason => String(reason.id) === value);
    setSearch(selected?.name ?? '');
    setHasEndDate(selected?.uid === PROMISE_OF_PAYMENT_UID);
    combobox.closeDropdown();
  };

  const minEndDate = dayjs().add(1, 'day').format('YYYY-MM-DD');

  return (
    <form
      onSubmit={form.onSubmit(async values => {
        if (hasEndDate && !values.end_at) {
          form.setFieldError('end_at', t(tKeys.ERROR.FIELD_REQUIRED));
          return;
        }
        setIsSubmitting(true);
        try {
          await onSubmit(hasEndDate ? values : { reason_id: values.reason_id });
        } finally {
          modals.closeAll();
        }
      })}
    >
      <Stack gap="md">
        <Combobox store={combobox} onOptionSubmit={handleOptionSubmit}>
          <Combobox.Target>
            <InputBase
              label={t(tKeys.FORM.REASON)}
              description={t(tKeys.ADD_REASON_HINT)}
              placeholder={t(tKeys.FORM.REASON)}
              rightSection={isCreateReasonLoading ? <Loader size="xs" /> : <Combobox.Chevron />}
              rightSectionPointerEvents="none"
              value={search}
              error={form.errors.reason_id}
              onChange={event => {
                combobox.openDropdown();
                combobox.updateSelectedOptionIndex();
                setSearch(event.currentTarget.value);
                isCreatingReasonRef.current = false;
                form.setFieldValue('reason_id', '');
              }}
              onClick={() => combobox.openDropdown()}
              onFocus={() => combobox.openDropdown()}
              onBlur={() => {
                combobox.closeDropdown();
                if (!form.values.reason_id) setSearch('');
              }}
            />
          </Combobox.Target>

          <Combobox.Dropdown>
            <Combobox.Options>
              {filteredReasons.map(reason => (
                <Combobox.Option key={reason.id} value={String(reason.id)}>
                  {reason.name}
                </Combobox.Option>
              ))}

              {canCreate && (
                <Combobox.Option value="$create" c="dimmed">
                  + {search.trim()}
                </Combobox.Option>
              )}

              {filteredReasons.length === 0 && !canCreate && (
                <Combobox.Empty>{t(tKeys.NO_RESULT)}</Combobox.Empty>
              )}
            </Combobox.Options>
          </Combobox.Dropdown>
        </Combobox>

        <Checkbox
          label={t(tKeys.FORM.ADD_END_DATE)}
          checked={hasEndDate}
          onChange={event => setHasEndDate(event.currentTarget.checked)}
        />

        <AnimatePresence>
          {hasEndDate && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              style={{ overflow: 'hidden' }}
            >
              <DatePickerInput
                label={t(tKeys.FORM.END_DATE)}
                placeholder={t(tKeys.FORM.SELECT_DATE)}
                minDate={minEndDate}
                value={form.values.end_at || null}
                error={form.errors.end_at}
                onChange={value => form.setFieldValue('end_at', value ?? '')}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <Right>
          <Button type="submit" loading={isSubmitting} disabled={!form.values.reason_id}>
            {t(tKeys.CONFIRM)}
          </Button>
        </Right>
      </Stack>
    </form>
  );
};
